// -----------------------------------------------------------------------------
// Central content for the portfolio. Edit here to update the site — no need to
// touch the components.
// -----------------------------------------------------------------------------

export const profile = {
  name: 'Purushothama G',
  role: 'Backend-focused Full-Stack Developer',
  tagline:
    'A backend-focused developer — I design robust APIs and services with NestJS, Node.js and PostgreSQL, deploy them with Docker, Kubernetes and AWS, and ship clean React + MUI interfaces on top.',
  location: 'Bengaluru, India',
  email: 'purushothamachar96@gmail.com',
  phone: '+91 77600 65791',
  resume: 'Purushothama_Resume.pdf', // in src/Assets
  socials: {
    github: 'https://github.com/PurushothamaG96',
    linkedin: 'https://linkedin.com/in/purushothama-g-2935b0204/',
  },
};

export const stats = [
  { value: '3', label: 'Years experience' },
  { value: 'Backend', label: 'Primary focus' },
  { value: 'NestJS', label: 'Core framework' },
  { value: 'AWS', label: 'Cloud & DevOps' },
];

// Grouped skills — each icon is a Font Awesome brand/solid class already bundled.
// Backend is my primary area; groups are ordered accordingly.
export const skillGroups = [
  {
    title: 'Backend (primary)',
    items: [
      { name: 'NestJS', icon: 'fa-solid fa-cubes' },
      { name: 'Node.js', icon: 'fa-brands fa-node-js' },
      { name: 'Express', icon: 'fa-solid fa-server' },
      { name: 'PostgreSQL', icon: 'fa-solid fa-database' },
      { name: 'TypeORM', icon: 'fa-solid fa-table' },
      { name: 'REST APIs', icon: 'fa-solid fa-arrow-right-arrow-left' },
      { name: 'WebSockets', icon: 'fa-solid fa-plug' },
      { name: 'Serverless', icon: 'fa-solid fa-bolt-lightning' },
      { name: 'JWT Auth', icon: 'fa-solid fa-key' },
      { name: 'MongoDB', icon: 'fa-solid fa-leaf' },
    ],
  },
  {
    title: 'Cloud & DevOps',
    items: [
      { name: 'AWS (EC2, S3, RDS, Lambda)', icon: 'fa-brands fa-aws' },
      { name: 'Docker', icon: 'fa-brands fa-docker' },
      { name: 'Kubernetes', icon: 'fa-solid fa-dharmachakra' },
      { name: 'Jenkins', icon: 'fa-solid fa-gears' },
      { name: 'CI / CD', icon: 'fa-solid fa-arrows-rotate' },
      { name: 'Git / GitHub', icon: 'fa-brands fa-git-alt' },
    ],
  },
  {
    title: 'Frontend',
    items: [
      { name: 'React', icon: 'fa-brands fa-react' },
      { name: 'MUI (Material UI)', icon: 'fa-solid fa-swatchbook' },
      { name: 'TypeScript', icon: 'fa-solid fa-code' },
      { name: 'JavaScript', icon: 'fa-brands fa-js' },
      { name: 'Redux Toolkit', icon: 'fa-solid fa-layer-group' },
      { name: 'Next.js', icon: 'fa-solid fa-n' },
      { name: 'HTML5', icon: 'fa-brands fa-html5' },
      { name: 'CSS3', icon: 'fa-brands fa-css3-alt' },
    ],
  },
  {
    title: 'Foundations',
    items: [
      { name: 'Data Structures', icon: 'fa-solid fa-diagram-project' },
      { name: 'Algorithms', icon: 'fa-solid fa-sitemap' },
    ],
  },
];

export const experience = [
  {
    company: 'Octalogic Tech LLP',
    role: 'Full-Stack Developer',
    project: 'Sports Odds & Predictions Platform',
    period: 'Aug 2023 — Present',
    type: 'Full-time',
    stack: ['NestJS', 'PostgreSQL', 'TypeORM', 'WebSockets', 'React', 'MUI', 'Docker', 'Jenkins', 'AWS'],
    points: [
      'Built and maintained RESTful APIs in NestJS with complex business logic for odds calculations and event predictions.',
      'Delivered real-time odds updates and event-status changes to the frontend over WebSockets.',
      'Designed and managed relational database schemas with PostgreSQL and TypeORM for efficient storage and retrieval.',
      'Built responsive, interactive UI components with ReactJS and Material UI for a seamless cross-device experience.',
      'Containerized microservices with Docker and automated build, test, and deploy via Jenkins CI/CD pipelines.',
      'Hosted and scaled the application on AWS (EC2, S3, RDS), writing end-to-end TypeScript with strict typing.',
    ],
  },
  {
    company: 'Octalogic Tech LLP',
    role: 'Full-Stack Developer',
    project: 'eCommerce Wine Platform',
    period: 'Jul 2023 — Aug 2023',
    type: 'Full-time',
    stack: ['Node.js', 'Serverless', 'PostgreSQL', 'TypeORM', 'React', 'MUI', 'Docker', 'AWS Lambda'],
    points: [
      'Built serverless backend services with Node.js and the Serverless Framework, improving scalability and reducing operational overhead.',
      'Developed Admin and Customer dashboards with ReactJS and Material UI for a smooth, responsive experience.',
      'Modeled schemas and queries with PostgreSQL and TypeORM; containerized services with Docker for reliable deployments.',
      'Deployed workloads on AWS (Lambda, S3, API Gateway, RDS) with Jenkins-automated pipelines.',
      'Applied full-stack TypeScript and performance tuning to cut page-load times and query latency.',
    ],
  },
  {
    company: 'Krtya (Freelance)',
    role: 'Freelance Web Developer',
    project: 'Classical Arts Education Platform',
    period: '2024',
    type: 'Freelance',
    stack: ['HTML5', 'CSS3', 'JavaScript', 'Hostinger'],
    points: [
      'Designed and built Krtya — an educational platform for Indian classical arts (Bharatanatyam, flute, Hindustani classical, and more).',
      'Delivered the site end to end with hand-written HTML, CSS, and JavaScript.',
      'Deployed and hosted the production site on Hostinger.',
    ],
  },
];

export const projects = [
  {
    title: 'Sports Odds & Predictions Platform',
    category: 'Work',
    company: 'Octalogic Tech LLP',
    description:
      'A real-time sports odds and predictions platform. Built NestJS REST APIs with complex odds-calculation logic and live WebSocket updates, backed by PostgreSQL and TypeORM, with a React + Material UI frontend, containerized with Docker and shipped via Jenkins on AWS.',
    tech: ['NestJS', 'PostgreSQL', 'TypeORM', 'WebSockets', 'React', 'MUI', 'Docker', 'Jenkins', 'AWS'],
    links: {},
    accent: 'blue',
    icon: 'fa-solid fa-chart-line',
  },
  {
    title: 'eCommerce Wine Platform',
    category: 'Work',
    company: 'Octalogic Tech LLP',
    description:
      'An e-commerce wine platform with Admin and Customer dashboards. Serverless Node.js backend on AWS Lambda, a PostgreSQL + TypeORM data layer, and a responsive React + Material UI interface, with Docker and Jenkins-automated deployments.',
    tech: ['Node.js', 'Serverless', 'PostgreSQL', 'TypeORM', 'React', 'MUI', 'AWS Lambda', 'Docker'],
    links: {},
    accent: 'pink',
    icon: 'fa-solid fa-wine-glass',
  },
  {
    title: 'NovaMart — E-commerce',
    category: 'Personal',
    description:
      'A modern e-commerce storefront with a persistent cart, live product search, category filters, pagination, and JWT auth. Built to showcase advanced state management.',
    tech: ['React 19', 'Redux Toolkit', 'RTK Query', 'TypeScript', 'Vite'],
    links: { code: 'https://github.com/PurushothamaG96/e-commerce' },
    accent: 'violet',
    icon: 'fa-solid fa-cart-shopping',
  },
  {
    title: 'WhatsApp Clone',
    category: 'Personal',
    description:
      'A full-featured real-time chat app: instant messaging, media sharing, message status, reactions, typing indicators, group chats, and online presence.',
    tech: ['Next.js 15', 'TypeScript', 'Socket.io', 'MongoDB', 'Zustand', 'Tailwind'],
    links: { code: 'https://github.com/PurushothamaG96/whatsup-clone' },
    accent: 'green',
    icon: 'fa-brands fa-whatsapp',
  },
  {
    title: 'Krtya — Classical Arts Platform',
    category: 'Client',
    description:
      'A freelance-built educational website for Indian classical arts — Bharatanatyam, flute, and Hindustani classical music. Delivered end to end and deployed on Hostinger.',
    tech: ['HTML5', 'CSS3', 'JavaScript', 'Hostinger'],
    links: { live: 'https://krtyaved-v2.onrender.com' },
    accent: 'amber',
    icon: 'fa-solid fa-music',
  },
];

export const education = [
  {
    school: '10x Academy',
    detail: 'Full-Stack Web Development — MERN Stack',
    icon: 'fa-solid fa-laptop-code',
  },
  {
    school: 'BGS Institute of Technology, BG Nagar',
    detail: 'B.E. in Mechanical Engineering — VTU',
    icon: 'fa-solid fa-graduation-cap',
  },
  {
    school: 'SET Polytechnic, Melkote',
    detail: 'Diploma in Mechanical Engineering — DTE',
    icon: 'fa-solid fa-school',
  },
  {
    school: 'SMR High School, Magadi',
    detail: 'Secondary School Leaving Certificate — KSEEB',
    icon: 'fa-solid fa-book',
  },
];
